import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useLocale } from '@hooks/useLocale';
import Link from 'next/link';
import Skeleton from 'react-loading-skeleton';
import { BreadcrumbJsonLd } from 'next-seo';

import styles from './Breadcrumbs.module.scss';

export interface Breadcrumb {
  name: string;
  path: string;
}

export interface BreadcrumbsProps {
  name?: string;
  breadcrumbs?: Breadcrumb[];
}

const Breadcrumbs = ({ name, breadcrumbs = [] }: BreadcrumbsProps) => {
  const { t } = useTranslation('common');
  const { isFallback } = useRouter();
  const locale = useLocale();
  const generateLink = (item: Breadcrumb) => {
    const path = `/${locale}${item.path}`;
    return <a href={path}>{item.name}</a>;
  };

  const homeHref = `/${locale}`;

  const jsonLdBreadcrumbs = [
    { name: t('breadcrumb.home'), path: homeHref },
    ...breadcrumbs,
  ];

  return (
    <>
      {breadcrumbs && (
        <BreadcrumbJsonLd
          itemListElements={jsonLdBreadcrumbs.map((item, i) => ({
            position: i,
            name: item.name,
            item: item.path,
          }))}
        />
      )}
      <nav className={styles.container} aria-label={t('breadcrumb.aria')}>
        <ol className={styles.list}>
          <li className={styles.listItem}>
            <Link href={homeHref}>
              {isFallback ? <Skeleton width={600} /> : t('breadcrumb.home')}
            </Link>
          </li>
          {breadcrumbs?.map((item) => (
            <li
              key={`breadcrumb-item-${item.name}`}
              className={styles.listItem}
            >
              {generateLink(item)}
            </li>
          ))}
          <li className={styles.listItem}>
            <span className={styles.lastItem} aria-current="page">
              {name}
            </span>
          </li>
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumbs;
